import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Search, FileText, Download, Filter, X, Eye } from "lucide-react";

export function UsersTable({ users, filters, search, setSearch, orgFilter, setOrgFilter, deptFilter, setDeptFilter, archetypeFilter, setArchetypeFilter, onExportPDF, onImpersonate }) {
    const navigate = useNavigate();
    const hasActiveFilters = orgFilter !== "all" || deptFilter !== "all" || archetypeFilter !== "all";

    const clearFilters = () => {
        setOrgFilter("all");
        setDeptFilter("all");
        setArchetypeFilter("all");
        setSearch("");
    };

    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            const q = search.toLowerCase();
            const matchesSearch = !q || u.name?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q) ||
                u.organization?.toLowerCase().includes(q) ||
                u.coach_name?.toLowerCase().includes(q) ||
                u.department?.toLowerCase().includes(q);
            const matchesOrg = orgFilter === "all" || u.organization === orgFilter;
            const matchesDept = deptFilter === "all" || u.department === deptFilter;
            const archetype = u.assessment?.scores?.archetype?.name;
            const matchesArchetype = archetypeFilter === "all" || archetype === archetypeFilter;
            return matchesSearch && matchesOrg && matchesDept && matchesArchetype;
        });
    }, [users, search, orgFilter, deptFilter, archetypeFilter]);

    const filteredStats = useMemo(() => {
        if (!hasActiveFilters && !search) return null;
        const completed = filteredUsers.filter(u => u.assessment?.status === "completed");
        const pending = filteredUsers.filter(u => u.assessment?.status !== "completed");
        const scores = completed.map(u => u.assessment?.scores?.total_weighted_score).filter(s => s != null);
        const avgScore = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : 0;
        return { total: filteredUsers.length, completed: completed.length, pending: pending.length, avgScore };
    }, [filteredUsers, hasActiveFilters, search]);

    return (
        <Card className="border-border bg-card">
            <CardHeader className="pb-3 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>
                        All Assessment Users
                    </CardTitle>
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input data-testid="admin-search-input" placeholder="Search users, coaches, org..."
                            value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-background border-border" />
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Filter className="h-4 w-4" /><span>Filters:</span>
                    </div>
                    <Select value={orgFilter} onValueChange={setOrgFilter}>
                        <SelectTrigger data-testid="filter-org" className="w-full sm:w-[180px] h-9 bg-background border-border text-sm">
                            <SelectValue placeholder="Organization" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Organizations</SelectItem>
                            {filters.organizations.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={deptFilter} onValueChange={setDeptFilter}>
                        <SelectTrigger data-testid="filter-dept" className="w-full sm:w-[180px] h-9 bg-background border-border text-sm">
                            <SelectValue placeholder="Department" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Departments</SelectItem>
                            {filters.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={archetypeFilter} onValueChange={setArchetypeFilter}>
                        <SelectTrigger data-testid="filter-archetype" className="w-full sm:w-[220px] h-9 bg-background border-border text-sm">
                            <SelectValue placeholder="Archetype" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Archetypes</SelectItem>
                            {filters.archetypes.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {hasActiveFilters && (
                        <Button data-testid="clear-filters-btn" variant="ghost" size="sm" onClick={clearFilters}
                            className="h-9 gap-1.5 text-muted-foreground hover:text-foreground">
                            <X className="h-3.5 w-3.5" /> Clear
                        </Button>
                    )}
                </div>

                {filteredStats && (
                    <div data-testid="filter-summary" className="flex items-center gap-4 text-sm px-3 py-2 rounded-md bg-primary/5 border border-primary/10">
                        <span className="text-muted-foreground">Showing <span className="font-semibold text-foreground">{filteredStats.total}</span> users</span>
                        <span className="text-muted-foreground">|</span>
                        <span className="text-emerald-600 font-medium">{filteredStats.completed} completed</span>
                        <span className="text-muted-foreground">{filteredStats.pending} pending</span>
                        {filteredStats.avgScore > 0 && (
                            <><span className="text-muted-foreground">|</span><span className="text-primary font-medium">Avg: {filteredStats.avgScore}</span></>
                        )}
                    </div>
                )}
            </CardHeader>
            <CardContent className="p-0">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-border">
                                <TableHead className="text-muted-foreground">Name</TableHead>
                                <TableHead className="text-muted-foreground">Coach</TableHead>
                                <TableHead className="text-muted-foreground">Organization</TableHead>
                                <TableHead className="text-muted-foreground">Department</TableHead>
                                <TableHead className="text-muted-foreground">Status</TableHead>
                                <TableHead className="text-muted-foreground">Score</TableHead>
                                <TableHead className="text-muted-foreground">Archetype</TableHead>
                                <TableHead className="text-muted-foreground">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsers.map((u) => {
                                const a = u.assessment;
                                const status = a?.status || "not_started";
                                const score = a?.scores?.total_weighted_score;
                                const archName = a?.scores?.archetype?.name;
                                return (
                                    <TableRow key={u.id} className="border-border hover:bg-secondary/50">
                                        <TableCell>
                                            <div>
                                                <p className="font-medium text-foreground text-sm">{u.name}</p>
                                                <p className="text-xs text-muted-foreground">{u.email}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{u.coach_name}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{u.organization || "-"}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{u.department || "-"}</TableCell>
                                        <TableCell>
                                            <Badge variant={status === "completed" ? "default" : status === "in_progress" ? "secondary" : "outline"}
                                                className={status === "completed" ? "bg-emerald-500/20 text-emerald-600 border-emerald-500/30" : ""}>
                                                {status.replace("_", " ")}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm font-medium text-foreground">{score != null ? score.toFixed(1) : "-"}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{archName || "-"}</TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                {status === "completed" && (
                                                    <>
                                                        <Button data-testid={`view-report-${u.id}`} variant="ghost" size="sm"
                                                            onClick={() => navigate(`/admin/reports/${u.id}`)} className="text-primary hover:text-primary/80">
                                                            <FileText className="h-4 w-4" />
                                                        </Button>
                                                        <Button data-testid={`export-pdf-${u.id}`} variant="ghost" size="sm"
                                                            onClick={() => onExportPDF(u.id)} className="text-muted-foreground hover:text-foreground">
                                                            <Download className="h-4 w-4" />
                                                        </Button>
                                                    </>
                                                )}
                                                <Button data-testid={`impersonate-${u.id}`} variant="ghost" size="sm"
                                                    onClick={() => onImpersonate(u.id)} title="View as this user"
                                                    className="text-amber-500 hover:text-amber-600">
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {filteredUsers.length === 0 && (
                                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No users found</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}
